import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plane, Hotel, Car, Hand, Columns2, LayoutList, Music } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import UploadDataModal from '../components/UploadDataModal'
import DashboardAssetsModal from '../components/DashboardAssetsModal'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import {
  getDataAPI,
  getEffectiveUserId,
  getActiveArtistIds,
  postDataAPI,
  setActiveArtistIds,
  setArtistCache,
} from '../lib/api'
import { useUserPermissions } from '../hooks/useUserPermissions'
import { normalizeList } from '../lib/normalize'
import { makeCacheKey, readSessionCache, writeSessionCache } from '../lib/sessionCache'
import { computeDashboardHeroCardModel } from '../lib/overviewHeroCardModel'
import DashboardHeroCard from '../components/DashboardHeroCard'
import { Skeleton } from '../components/ui/Skeleton'
import {
  computeIntelligence,
  parseBonusThreshold,
  type IntelligenceSignal,
  type ShowData,
  type SongData,
  type FinanceData,
  type TaskData,
} from '../lib/intelligenceEngine'
import {
  normalizeDashboardTasksFromPayload,
  getTaskAssigneeIds,
  taskNeedsApproval,
  formatDuePhrase,
  taskDueInNextWeek,
  isTaskCompleted,
  type DashboardTask,
} from '../lib/taskDashboardUtils'
import { parseLocalDate, toLocalDateKey, shortDateDisplay } from '../lib/dateUtils'
import { summarizeMoneyPeriod, listFinancesInPeriod, type MoneyRangeId } from '../lib/dashboardMoney'
import {
  extractArray,
  normalizeBookings,
  normalizeCatalog,
  normalizeFinancials,
  deriveShowFinanceEntries,
  deriveDealFinanceEntries,
  mergeUniqueFinances,
  isUpcomingSong,
} from '../lib/dashboardNormalize'
import { GlassCard, SlideTag, StatRow } from './wrapped/WrappedSlides'

/** Matches digest show-detail ticket bar low-sell highlight. */
const BRAND = '#FF2300'

function ShowStatusBadge({ status, label }: { status: 'red' | 'yellow' | 'green' | 'blue'; label: string }) {
  const colors = {
    red: 'bg-red-500/20 text-red-400',
    yellow: 'bg-amber-500/20 text-amber-400',
    green: 'bg-emerald-500/20 text-emerald-400',
    blue: 'bg-sky-500/20 text-sky-400',
  }
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${colors[status]}`}>{label}</span>
  )
}

function parseShowSellableCap(show: ShowData | undefined): number {
  if (!show) return 0
  const raw = show.sellable_cap ?? show.capacity ?? 0
  return Number(String(raw).replace(/[^0-9]/g, '')) || 0
}

/** Matches Shows list / overview — omit canceled shows from dashboard pacing tiles. */
function isShowCanceled(show: ShowData | undefined): boolean {
  if (!show) return false
  const s = (show.status || '').toLowerCase().trim()
  if (s === 'canceled' || s === 'cancelled') return true
  try {
    const raw = show.raw as Record<string, unknown> | undefined
    const st = raw && typeof raw.status === 'string' ? raw.status.toLowerCase().trim() : ''
    return st === 'canceled' || st === 'cancelled'
  } catch {
    return false
  }
}

/** Persisted so refresh keeps Intelligence dashboard artist scope (single id or "all"). */
const INTEL_SCOPE_STORAGE_KEY = 'dizee:intelDashboardScope'
const ALL_ARTISTS_VALUE = '__all__'

/** Warm the digest chunk so “See Digest” rarely waits on network. */
function prefetchDigestPage() {
  void import('./DigestPage')
}

function mergeRawPayloadArrays(payloads: unknown[]): unknown[] {
  const seen = new Set<string>()
  const out: unknown[] = []
  for (const payload of payloads) {
    for (const item of extractArray(payload)) {
      const row = item as { _id?: string; id?: string }
      const id = String(row._id ?? row.id ?? '')
      if (id) {
        if (seen.has(id)) continue
        seen.add(id)
      }
      out.push(item)
    }
  }
  return out
}

function mergeDealsPayloads(parts: unknown[]): unknown {
  const deals: unknown[] = []
  const seen = new Set<string>()
  for (const payload of parts) {
    const raw = (payload as { data?: { deals?: unknown[] }; deals?: unknown[] })?.data ?? payload
    const list = Array.isArray((raw as { deals?: unknown[] })?.deals)
      ? (raw as { deals: unknown[] }).deals
      : Array.isArray(raw)
        ? raw
        : []
    for (const deal of list) {
      const rec = deal as { _id?: string; id?: string }
      const id = String(rec._id ?? rec.id ?? '')
      if (id) {
        if (seen.has(id)) continue
        seen.add(id)
      }
      deals.push(deal)
    }
  }
  return { data: { deals } }
}

function mergeCalendarPayloads(parts: (unknown | null)[]): { data: { data: { events: unknown[] } } } {
  const events: unknown[] = []
  const seen = new Set<string>()
  for (const raw of parts) {
    if (!raw) continue
    const p = raw as { data?: { data?: { events?: unknown[] }; events?: unknown[] }; events?: unknown[] }
    const list =
      p?.data?.data?.events ??
      (p?.data as { events?: unknown[] })?.events ??
      p?.events ??
      []
    const arr = Array.isArray(list) ? list : []
    for (const e of arr) {
      const rec = e as { id?: string; iCalUID?: string }
      const id = String(rec.id ?? rec.iCalUID ?? '')
      const key = id || `e-${events.length}`
      if (seen.has(key)) continue
      seen.add(key)
      events.push(e)
    }
  }
  return { data: { data: { events } } }
}

function clearSessionStorageExceptIntelScope() {
  try {
    const scope = sessionStorage.getItem(INTEL_SCOPE_STORAGE_KEY)
    sessionStorage.clear()
    if (scope) sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, scope)
  } catch {
    sessionStorage.clear()
  }
}

/** Single scroll region per SectionCard — children should not nest another overflow-y-auto (breaks wheel/touch scrolling). */
const MODULE_BODY_SCROLL =
  'flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-y-contain intel-scroll'

const MODULE_BODY_INNER = 'min-w-0 -mx-1 px-1'

/** Digest-style primary title (matches Shows pacing rows). */
const DASH_H2 = 'text-xl font-semibold tracking-tight text-gray-900 dark:text-white'

const DASH_META = 'text-[11px] text-gray-600 dark:text-zinc-500'
const DASH_SUB = 'text-[12px] text-gray-600 dark:text-zinc-400'
const DASH_LABEL = 'text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500'
const DASH_VALUE = 'text-[13px] font-semibold text-gray-900 dark:text-white'

const DASHBOARD_MODULE_LAYOUT_KEY = 'dizee:dashboardModuleLayout'
const DASHBOARD_MODULE_ORDER_KEY = 'dizee:dashboardModuleOrder'

type DashboardModuleId = 'shows' | 'music' | 'tasks' | 'money'
type DashboardModuleLayout = 'sideBySide' | 'stacked'

const ALL_MODULE_IDS: DashboardModuleId[] = ['shows', 'music', 'tasks', 'money']

const MODULE_TITLES: Record<DashboardModuleId, string> = {
  shows: 'Shows',
  music: 'Music',
  tasks: 'Tasks',
  money: 'Money',
}

function normalizeDashboardModuleOrder(raw: unknown): DashboardModuleId[] {
  const valid = new Set<DashboardModuleId>(ALL_MODULE_IDS)
  const seen = new Set<DashboardModuleId>()
  const out: DashboardModuleId[] = []
  if (Array.isArray(raw)) {
    for (const x of raw) {
      const id = x as DashboardModuleId
      if (valid.has(id) && !seen.has(id)) {
        seen.add(id)
        out.push(id)
      }
    }
  }
  for (const id of ALL_MODULE_IDS) {
    if (!seen.has(id)) out.push(id)
  }
  return out
}

/** Layout toggle + drag reorder only on desktop (Tailwind `lg` / 1024px). */
const DASHBOARD_MODULE_DESKTOP_MQ = '(min-width: 1024px)'

function useDashboardModuleDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(DASHBOARD_MODULE_DESKTOP_MQ).matches
  })
  useEffect(() => {
    const mq = window.matchMedia(DASHBOARD_MODULE_DESKTOP_MQ)
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    setIsDesktop(mq.matches)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

function songMetadataPills(song: SongData | undefined): { label: string; value: string }[] {
  if (!song) return []
  const raw = (song.raw || {}) as Record<string, unknown>
  const isrc = raw.isrc ?? raw.ISRC
  const genre = raw.genre ?? raw.Genre
  const duration = raw.duration ?? raw.Duration
  const label = raw.label ?? raw.Label
  const pills: { label: string; value: string }[] = []
  if (isrc) pills.push({ label: 'ISRC', value: String(isrc) })
  if (genre) pills.push({ label: 'Genre', value: String(genre) })
  if (duration != null && duration !== '') pills.push({ label: 'Duration', value: String(duration) })
  if (label) pills.push({ label: 'Label', value: String(label) })
  return pills
}

type HeroRawBundle = {
  shows: unknown
  songs: unknown
  tasks: unknown
  calendar: unknown
}

/**
 * Dashboard module shell — light surfaces in light mode; digest (#13120A) in dark mode.
 * No orange sliver — that stays on digest-only slides.
 */
function SectionCard({
  title,
  children,
  headerEnd,
}: {
  title: string
  children: React.ReactNode
  /** Top-right slot (e.g. drag handle). */
  headerEnd?: React.ReactNode
}) {
  return (
    <div className="w-full min-w-0">
      <div
        className="relative flex w-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/[0.08] dark:bg-[#13120A] dark:shadow-none"
        style={{ height: 580 }}
      >
        <div className="flex h-full min-h-0 flex-col px-6 pb-8 pt-6 text-gray-900 dark:text-white">
          <div className="flex shrink-0 items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 flex-1 pt-0.5">
              <SlideTag>{title}</SlideTag>
            </div>
            {headerEnd}
          </div>
          <div className={`mt-3 ${MODULE_BODY_SCROLL}`}>{children}</div>
        </div>
      </div>
    </div>
  )
}

function SortableModuleSection({
  id,
  title,
  children,
}: {
  id: DashboardModuleId
  title: string
  children: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.92 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} className="w-full min-w-0">
      <SectionCard
        title={title}
        headerEnd={
          <button
            type="button"
            className="group shrink-0 -mr-1 -mt-1 cursor-grab touch-none rounded-xl p-2 transition-colors hover:bg-gray-100/90 active:cursor-grabbing dark:hover:bg-white/[0.06]"
            aria-label="Drag to reorder module"
            {...attributes}
            {...listeners}
          >
            <Hand
              className="pointer-events-none h-4 w-4 text-gray-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100 dark:text-zinc-400"
              aria-hidden
              strokeWidth={1.75}
            />
          </button>
        }
      >
        {children}
      </SectionCard>
    </div>
  )
}

function DashboardShowsCombined({
  pacingSignals,
  riskSignals,
  showsById,
  hideFinancials = false,
  tmFlights,
  tmHotels,
  tmGroundTransport,
}: {
  pacingSignals: IntelligenceSignal[]
  riskSignals: IntelligenceSignal[]
  showsById: Map<string, ShowData>
  hideFinancials?: boolean
  tmFlights: unknown[]
  tmHotels: unknown[]
  tmGroundTransport: unknown[]
}) {
  const atRiskShowIds = useMemo(() => {
    const s = new Set<string>()
    for (const r of riskSignals) {
      if (r.source === 'shows' && r.relatedEntityId) s.add(String(r.relatedEntityId))
    }
    return s
  }, [riskSignals])

  const visiblePacingSignals = useMemo(
    () =>
      pacingSignals.filter((sig) => {
        const id = sig.relatedEntityId ? String(sig.relatedEntityId) : ''
        const show = id ? showsById.get(id) : undefined
        return !isShowCanceled(show)
      }),
    [pacingSignals, showsById],
  )

  /**
   * Tiles that are not in pacing: (1) ticket tracking off, (2) tracking on but no sellable cap.
   * Pacing signals only exist when cap &gt; 0, so upcoming “next shows” like the hero would still omit (2) without this.
   */
  const secondaryShowRows = useMemo(() => {
    const inPacing = new Set(
      visiblePacingSignals.map((s) => (s.relatedEntityId ? String(s.relatedEntityId) : '')).filter(Boolean),
    )
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const isUpcoming = (show: ShowData) => {
      const d = parseLocalDate(show.date || show.perf_date || '')
      return Boolean(d && d >= today)
    }

    const trackingOff = Array.from(showsById.values())
      .filter((show) => {
        const showId = String(show.id)
        if (isShowCanceled(show)) return false
        if (inPacing.has(showId)) return false
        if (show.ticket_tracking !== false) return false
        return isUpcoming(show)
      })
      .sort(
        (a, b) =>
          (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
          (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
      )
      .slice(0, 8)

    const toRow = (show: ShowData, variant: 'trackingOff' | 'needsCapacity') => {
      const showDate = parseLocalDate(show.date || show.perf_date || '')
      const label = show.title || show.event_title || show.venue || 'Show'
      const city = show.city ? ` — ${show.city}` : ''
      const daysUntil = showDate
        ? Math.max(0, Math.ceil((showDate.getTime() - today.getTime()) / 86_400_000))
        : null
      return {
        id: String(show.id),
        title: `${label}${city}`,
        daysUntil,
        variant,
      }
    }

    const trackingOffRows = trackingOff.map((s) => toRow(s, 'trackingOff'))
    const trackingOffIds = new Set(trackingOffRows.map((r) => r.id))

    const needsCapacity = Array.from(showsById.values())
      .filter((show) => {
        const showId = String(show.id)
        if (isShowCanceled(show)) return false
        if (inPacing.has(showId)) return false
        if (trackingOffIds.has(showId)) return false
        if (show.ticket_tracking === false) return false
        if (parseShowSellableCap(show) > 0) return false
        return isUpcoming(show)
      })
      .sort(
        (a, b) =>
          (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
          (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
      )
      .slice(0, 8)

    const needsCapacityRows = needsCapacity.map((s) => toRow(s, 'needsCapacity'))

    return [...trackingOffRows, ...needsCapacityRows]
  }, [visiblePacingSignals, showsById])

  const mergedShowRows = useMemo(() => {
    const pacingPart = visiblePacingSignals.map((signal) => {
      const showId = signal.relatedEntityId ? String(signal.relatedEntityId) : ''
      const show = showId ? showsById.get(showId) : undefined
      const d = show ? parseLocalDate(show.date || show.perf_date || '') : null
      const sortTime = d?.getTime() ?? Number.MAX_SAFE_INTEGER
      return { kind: 'pacing' as const, signal, sortTime }
    })
    const ntPart = secondaryShowRows.map((row) => {
      const show = showsById.get(row.id)
      const d = show ? parseLocalDate(show.date || show.perf_date || '') : null
      const sortTime = d?.getTime() ?? Number.MAX_SAFE_INTEGER
      return { kind: 'notTracking' as const, row, sortTime }
    })
    return [...pacingPart, ...ntPart].sort((a, b) => a.sortTime - b.sortTime)
  }, [visiblePacingSignals, secondaryShowRows, showsById])

  if (mergedShowRows.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
        No upcoming shows in this view yet. Add a sellable capacity on a booking for ticket pacing, or turn ticket
        tracking off to list it here without pacing.
      </p>
    )
  }

  return (
    <div className={MODULE_BODY_INNER}>
      <div className="space-y-5">
        {mergedShowRows.map((item) => {
          if (item.kind === 'pacing') {
            const { signal } = item
            const showId = signal.relatedEntityId ? String(signal.relatedEntityId) : ''
            const atRisk = showId && atRiskShowIds.has(showId)
            const show = showId ? showsById.get(showId) : undefined
            const bonusParsed = show?.bonus ? parseBonusThreshold(show.bonus) : null
            const bonusSold = bonusParsed ? Number(show?.tix_sold ?? show?.ticket_count ?? 0) || 0 : 0
            const bonusThreshold = bonusParsed?.threshold ?? 1
            const bonusProgress = bonusParsed ? Math.min(100, Math.round((bonusSold / bonusThreshold) * 100)) : 0
            const bonusUnlocked = bonusParsed ? bonusSold >= bonusThreshold : false

            const primaryTitle = show?.event_title || show?.title || show?.venue || signal.title
            const sold = Number(show?.tix_sold ?? show?.ticket_count ?? 0) || 0
            const cap = parseShowSellableCap(show)
            const ticketPct = cap > 0 ? Math.round((sold / cap) * 100) : 0
            const guarantee = show?.guarantee ? Number(String(show.guarantee).replace(/[^0-9.]/g, '')) : 0
            const daysUntil = signal.meta?.daysUntil ?? '?'
            const isPositive = (signal.delta ?? 0) >= 0
            const dateStr = show ? shortDateDisplay(show.date || show.perf_date || '') : ''

            const showHref = showId ? `/shows?focus=${encodeURIComponent(showId)}` : signal.link || '/shows'

            return (
              <Link key={signal.id} to={showHref} className="block">
              <h2 className={DASH_H2}>{primaryTitle}</h2>
              <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${DASH_META}`}>
                {show?.city && (
                  <span>
                    {show.city}
                    {show.state ? `, ${show.state}` : ''}
                  </span>
                )}
                {dateStr && <span>{dateStr}</span>}
                {cap > 0 && <span>Cap: {cap.toLocaleString()}</span>}
                {atRisk && <ShowStatusBadge status="red" label="At risk" />}
                <span className="text-gray-600 dark:text-zinc-600">{daysUntil}d out</span>
              </div>
              {signal.comparison && (
                <p className={`mt-2 text-[12px] ${isPositive ? 'text-emerald-400' : 'text-amber-400'}`}>{signal.comparison}</p>
              )}

              <div className="mt-3 space-y-2">
                {cap > 0 && (
                  <GlassCard className="p-3">
                    <div className="mb-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                        Ticket sales
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/[0.06]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, Math.max(2, ticketPct))}%`,
                          backgroundColor: ticketPct >= 70 ? '#22c55e' : ticketPct >= 40 ? '#f59e0b' : BRAND,
                        }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] text-gray-600 dark:text-zinc-600">{ticketPct}% sold</p>
                  </GlassCard>
                )}

                {!hideFinancials && guarantee > 0 && (
                  <GlassCard className="p-3">
                    <StatRow label="Guarantee" value={`$${guarantee.toLocaleString()}`} accent />
                  </GlassCard>
                )}

                {!hideFinancials && bonusParsed && (
                  <GlassCard className="p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className={DASH_LABEL}>Bonus tracking</span>
                      {bonusUnlocked ? (
                        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/20">
                          Earned
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-amber-400">{bonusProgress}%</span>
                      )}
                    </div>
                    <div className="mb-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/[0.06]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${bonusProgress}%`,
                          backgroundColor: bonusUnlocked ? '#22c55e' : '#f59e0b',
                        }}
                      />
                    </div>
                    <p className="text-[11px] text-gray-600 dark:text-zinc-500">
                      {`${bonusSold.toLocaleString()} / ${bonusThreshold.toLocaleString()} toward $${bonusParsed.amount.toLocaleString()} bonus`}
                    </p>
                  </GlassCard>
                )}

                <GlassCard className="p-3">
                  <p className={`mb-2 ${DASH_LABEL}`}>Travel</p>
                  <div className="space-y-1.5">
                    {[
                      { icon: Plane, label: 'Flight', hasData: tmFlights.length > 0 },
                      { icon: Hotel, label: 'Hotel', hasData: tmHotels.length > 0 },
                      { icon: Car, label: 'Ground', hasData: tmGroundTransport.length > 0 },
                    ].map(({ icon: Icon, label, hasData }) => (
                      <div key={label} className="flex items-center justify-between text-[11px]">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3 w-3 text-gray-500 dark:text-zinc-600" />
                          <span className="text-gray-600 dark:text-zinc-400">{label}</span>
                        </div>
                        <ShowStatusBadge status={hasData ? 'green' : 'red'} label={hasData ? 'Booked' : 'Missing'} />
                      </div>
                    ))}
                  </div>
                </GlassCard>

                <GlassCard className="p-3">
                  <p className={`mb-2 ${DASH_LABEL}`}>Crew</p>
                  <p className="text-[11px] text-gray-600 dark:text-zinc-600">No crew data configured yet.</p>
                </GlassCard>
              </div>
              </Link>
            )
          }

          const { row } = item
          const variant = row.variant ?? 'trackingOff'
          const show = showsById.get(row.id)
          const dateStr = show ? shortDateDisplay(show.date || show.perf_date || '') : ''
          const sellableCap = parseShowSellableCap(show)

          return (
            <Link key={`secondary-${variant}-${row.id}`} to={`/shows?focus=${encodeURIComponent(row.id)}`} className="block">
              <div className="flex items-start justify-between gap-2">
                <h2 className={DASH_H2}>{row.title}</h2>
                {variant === 'trackingOff' ? (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-600 bg-gray-100 dark:bg-white/[0.08] dark:text-zinc-400">
                    Tracking off
                  </span>
                ) : (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700 bg-sky-100 dark:bg-sky-500/15 dark:text-sky-300">
                    Needs sellable cap
                  </span>
                )}
              </div>
              <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${DASH_META}`}>
                {dateStr && <span>{dateStr}</span>}
                {row.daysUntil != null && <span>{row.daysUntil}d out</span>}
              </div>

              <div className="mt-3 space-y-2">
                {variant === 'needsCapacity' ? (
                  <GlassCard className="p-3">
                    <p className={`text-[11px] leading-relaxed ${DASH_SUB}`}>
                      Set a sellable capacity on this booking to see ticket pacing and % sold here.
                    </p>
                  </GlassCard>
                ) : null}
                {sellableCap > 0 && (
                  <GlassCard className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                        Capacity
                      </span>
                      <span className={`${DASH_VALUE} tabular-nums`}>{sellableCap.toLocaleString()}</span>
                    </div>
                  </GlassCard>
                )}

                <GlassCard className="p-3">
                  <p className={`mb-2 ${DASH_LABEL}`}>Travel</p>
                  <div className="space-y-1.5">
                    {[
                      { icon: Plane, label: 'Flight', hasData: tmFlights.length > 0 },
                      { icon: Hotel, label: 'Hotel', hasData: tmHotels.length > 0 },
                      { icon: Car, label: 'Ground', hasData: tmGroundTransport.length > 0 },
                    ].map(({ icon: Icon, label, hasData }) => (
                      <div key={`${row.id}-${label}`} className="flex items-center justify-between text-[11px]">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3 w-3 text-gray-500 dark:text-zinc-600" />
                          <span className="text-gray-600 dark:text-zinc-400">{label}</span>
                        </div>
                        <ShowStatusBadge status={hasData ? 'green' : 'red'} label={hasData ? 'Booked' : 'Missing'} />
                      </div>
                    ))}
                  </div>
                </GlassCard>

                <GlassCard className="p-3">
                  <p className={`mb-2 ${DASH_LABEL}`}>Crew</p>
                  <p className="text-[11px] text-gray-600 dark:text-zinc-600">No crew data configured yet.</p>
                </GlassCard>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function DashboardMusicCombined({
  catalogSongs,
  streamingSignals,
}: {
  catalogSongs: SongData[]
  streamingSignals: IntelligenceSignal[]
}) {
  const songById = useMemo(() => new Map(catalogSongs.map((s) => [String(s.id), s])), [catalogSongs])

  const rows = useMemo(() => {
    const ids = new Set<string>()
    for (const sig of streamingSignals) {
      if (sig.relatedEntityId) ids.add(String(sig.relatedEntityId))
    }
    for (const s of catalogSongs) {
      const id = String(s.id)
      if (isUpcomingSong(s)) ids.add(id)
    }
    const list = Array.from(ids)
      .map((id) => {
        const song = songById.get(id)
        const streamSig = streamingSignals.find((x) => String(x.relatedEntityId) === id)
        return { id, song, streamSig }
      })
      .filter((r) => r.song || r.streamSig)

    list.sort((a, b) => {
      const da = a.song?.release_date ? parseLocalDate(a.song.release_date)?.getTime() ?? 0 : 0
      const db = b.song?.release_date ? parseLocalDate(b.song.release_date)?.getTime() ?? 0 : 0
      if (da !== db) return db - da
      const sa = Number(a.streamSig?.meta?.streamsTotal ?? a.song?.streams_total ?? 0) || 0
      const sb = Number(b.streamSig?.meta?.streamsTotal ?? b.song?.streams_total ?? 0) || 0
      return sb - sa
    })
    return list
  }, [catalogSongs, streamingSignals])

  if (rows.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
        No music activity in this view yet.
      </p>
    )
  }

  return (
    <div className={MODULE_BODY_INNER}>
      <div className="space-y-5">
        {rows.map(({ id, song, streamSig }) => {
          const name = streamSig?.title || song?.song_title || song?.title || 'Track'
          const streamsToday = streamSig?.meta?.streamsToday ?? song?.streams_today ?? 0
          const streamsTotal = streamSig?.meta?.streamsTotal ?? song?.streams_total ?? 0
          const upcoming = song ? isUpcomingSong(song) : false
          const pills = songMetadataPills(song)
          const artworkUrl = (() => {
            const raw = song?.raw || {}
            const a = (raw.artwork || raw.album_artwork || raw.albumArtwork || '').toString().trim()
            if (!a || a === 'null' || a === 'undefined') return null
            return a
          })()

          return (
            <Link key={id} to={`/music?focus=${encodeURIComponent(id)}`} className="block">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-white/[0.06]">
                  {artworkUrl ? (
                    <img src={artworkUrl} alt={name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Music className="h-5 w-5 text-gray-400 dark:text-zinc-600" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className={DASH_H2}>{name}</h2>
                  {song?.artist_name && <p className={`mt-0.5 text-[12px] ${DASH_SUB}`}>{song.artist_name}</p>}
                  {song?.release_date && <p className={`mt-0.5 text-[11px] ${DASH_META}`}>{shortDateDisplay(song.release_date)}</p>}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {upcoming && (
                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700 bg-sky-100 dark:bg-sky-500/20 dark:text-sky-300">
                    Upcoming
                  </span>
                )}
              </div>
              {pills.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pills.map((p) => (
                    <span
                      key={`${id}-${p.label}`}
                      className="rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-medium text-gray-600 dark:bg-white/[0.06] dark:text-zinc-400"
                    >
                      {p.label}: {p.value}
                    </span>
                  ))}
                </div>
              )}
              {streamSig?.comparison && <p className={`mt-2 leading-relaxed ${DASH_SUB}`}>{streamSig.comparison}</p>}
              <div className="mt-3 space-y-2">
                <GlassCard className="p-3 text-center">
                  <p className="text-lg font-semibold tabular-nums tracking-tight leading-none text-gray-900 dark:text-white">
                    {streamsTotal.toLocaleString()}
                  </p>
                  <p className={`mt-1.5 text-[11px] font-medium ${DASH_META}`}>Total streams</p>
                </GlassCard>
                {streamsToday > 0 && (
                  <GlassCard className="p-3 text-center">
                    <p className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">
                      {streamsToday.toLocaleString()}
                    </p>
                    <p className={`mt-1 text-[10px] font-medium ${DASH_META}`}>Streams today</p>
                  </GlassCard>
                )}
                <GlassCard className="p-3">
                  <p className={`mb-1.5 ${DASH_LABEL}`}>Platform breakdown</p>
                  <p className={`text-[12px] leading-relaxed ${DASH_SUB}`}>
                    Per-platform stats available when connected.
                  </p>
                </GlassCard>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function DashboardTasksWeek({
  tasks,
  memberMap,
}: {
  tasks: DashboardTask[]
  memberMap: Record<string, string>
}) {
  const [openTask, setOpenTask] = useState<DashboardTask | null>(null)

  const weekTasks = useMemo(() => {
    return tasks
      .filter((t) => !isTaskCompleted(t) && t.due_date && taskDueInNextWeek(t.due_date))
      .sort((a, b) => {
        const da = toLocalDateKey(parseLocalDate(a.due_date) || new Date(0))
        const db = toLocalDateKey(parseLocalDate(b.due_date) || new Date(0))
        return da.localeCompare(db)
      })
  }, [tasks])

  const resolveName = useCallback(
    (uid: string) => {
      const n = memberMap[uid]
      if (n) return n
      return uid.length > 8 ? `${uid.slice(0, 6)}…` : uid
    },
    [memberMap],
  )

  if (weekTasks.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">No tasks due in the next week.</p>
    )
  }

  return (
    <>
      <div className={MODULE_BODY_INNER}>
        <div className="space-y-5">
          {weekTasks.map((t) => {
            const ids = getTaskAssigneeIds(t)
            const first = ids[0]
            const rest = Math.max(0, ids.length - 1)
            const approval = taskNeedsApproval(t)
            const duePhrase = formatDuePhrase(t.due_date)
            const taskRowId = String((t as { _id?: string })._id || t.id || '')

            return (
              <div key={t.id} className="block">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <Link
                    to={taskRowId ? `/tasks?focus=${encodeURIComponent(taskRowId)}` : '/tasks'}
                    className="min-w-0 flex-1 rounded-2xl px-2.5 py-2.5 transition-[background-color,box-shadow] duration-200 ease-out hover:bg-gray-100/65 hover:shadow-[0_1px_0_rgba(0,0,0,0.03)] dark:hover:bg-white/[0.045] dark:hover:shadow-[0_1px_0_rgba(255,255,255,0.04)]"
                  >
                    <h2 className={DASH_H2}>{t.title}</h2>
                    <p className={`mt-1 text-[11px] ${DASH_META}`}>{duePhrase}</p>
                  </Link>
                </div>
                <GlassCard className="mt-3 p-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {approval && (
                      <span className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 dark:bg-violet-500/20 dark:text-violet-300">
                        Approval needed
                      </span>
                    )}
                    {ids.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setOpenTask(t)}
                        className="min-w-0 text-left text-[11px] text-gray-600 underline-offset-2 transition-colors hover:text-gray-900 hover:underline dark:text-zinc-400 dark:hover:text-white"
                      >
                        Assigned to {first ? resolveName(first) : '—'}
                        {rest > 0 ? ` +${rest}` : ''}
                      </button>
                    )}
                  </div>
                </GlassCard>
              </div>
            )
          })}
        </div>
      </div>
      {openTask && (
        <Modal isOpen={!!openTask} onClose={() => setOpenTask(null)} title="Assigned to" maxWidthClassName="max-w-sm" mobileCentered>
          <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-2">
            {getTaskAssigneeIds(openTask).map((uid) => (
              <li key={uid}>{resolveName(uid)}</li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  )
}

function DashboardMoneySection({
  finances,
  hideFinancials,
}: {
  finances: FinanceData[]
  /** When true (team member without overview financial access), show shell only — no amounts or activity. */
  hideFinancials: boolean
}) {
  const [range, setRange] = useState<MoneyRangeId>('30d')
  const currentBalance = useMemo(() => {
    try {
      const uid = getEffectiveUserId() || 'default'
      const raw = localStorage.getItem(`dizee:money:currentBalance:${uid}`)
      if (raw == null || raw === '') return 0
      const n = Number(raw)
      return Number.isFinite(n) ? n : 0
    } catch {
      return 0
    }
  }, [])
  const summary = useMemo(() => summarizeMoneyPeriod(finances, range, currentBalance), [finances, range, currentBalance])

  const periodTx = useMemo(() => listFinancesInPeriod(finances, range), [finances, range])
  const txCount = periodTx.length

  const fmt = (n: number) => {
    const safe = Number.isFinite(n) ? n : 0
    return safe.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const filters: { id: MoneyRangeId; label: string }[] = [
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '6m', label: '6 months' },
  ]

  if (hideFinancials) {
    return (
      <div className="flex min-h-0 min-w-0 flex-col">
        <GlassCard className="p-3">
          <p className="text-[13px] leading-relaxed text-gray-700 dark:text-zinc-300">
            Financial details aren&apos;t shared with your role for this artist profile.
          </p>
          <p className="mt-2 text-[12px] leading-relaxed text-gray-600 dark:text-zinc-500">
            Balances and transactions stay hidden until the artist enables financial access for you in Team permissions. You can still use the rest of the dashboard.
          </p>
        </GlassCard>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setRange(f.id)}
            className={`rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors ${
              range === f.id
                ? 'bg-gray-900 text-white dark:bg-white/[0.08] dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-zinc-600 dark:hover:text-zinc-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <GlassCard className="mb-3 p-3">
        <StatRow label="Starting balance" value={fmt(summary.startingBalance)} />
        <div className="flex items-baseline justify-between border-b border-gray-200 py-2.5 dark:border-white/[0.04]">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Money in</span>
          <span className="text-[15px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{fmt(summary.moneyIn)}</span>
        </div>
        <div className="flex items-baseline justify-between border-b border-gray-200 py-2.5 dark:border-white/[0.04]">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Money out</span>
          <span className="text-[15px] font-semibold tabular-nums text-red-600 dark:text-red-400">{fmt(summary.moneyOut)}</span>
        </div>
        <div className="flex items-baseline justify-between pt-2.5">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Net</span>
          <span
            className={`text-[15px] font-semibold tabular-nums ${
              summary.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {summary.net >= 0 ? '+' : ''}
            {fmt(summary.net)}
          </span>
        </div>
      </GlassCard>

      <p className={`mb-2 shrink-0 ${DASH_LABEL}`}>Activity</p>
      <div className={MODULE_BODY_INNER}>
        {txCount === 0 ? (
          <p className="py-1 text-[13px] text-gray-500 dark:text-zinc-500">No transactions in this period.</p>
        ) : (
          <GlassCard className="p-3">
            <ul className="space-y-2.5">
              {periodTx.map((f, idx) => {
                const isRev = f.type === 'revenue'
                const amt = Number(f.amount) || 0
                return (
                  <li
                    key={`${f.id}-${idx}`}
                    className="flex items-start justify-between gap-2 border-b border-gray-200 pb-3 text-[12px] last:border-0 last:pb-0 dark:border-white/[0.04]"
                  >
                    <div className="min-w-0">
                      <span className="text-gray-500 dark:text-zinc-500">{shortDateDisplay(f.date)}</span>
                      <span className="mx-1 text-gray-400 dark:text-zinc-600">·</span>
                      <span className="break-words text-gray-800 dark:text-zinc-300">
                        {f.description || f.source || (isRev ? 'Revenue' : 'Spend')}
                      </span>
                    </div>
                    <span
                      className={`shrink-0 tabular-nums font-semibold ${
                        isRev ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {isRev ? '+' : '−'}
                      {fmt(amt)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </GlassCard>
        )}
      </div>
    </div>
  )
}

type ManagedArtistRow = {
  _id: string
  name: string
  email?: string
  role: string
  accountType?: 'artist' | 'promoter'
}

export default function DashboardOverviewPage() {
  const navigate = useNavigate()
  const [artistScopeTick, setArtistScopeTick] = useState(0)
  useEffect(() => {
    const onScope = () => setArtistScopeTick((t) => t + 1)
    window.addEventListener('dizee:activeArtistsChanged', onScope)
    return () => window.removeEventListener('dizee:activeArtistsChanged', onScope)
  }, [])

  const userId = getEffectiveUserId()
  const { isFinancialHidden } = useUserPermissions()
  /** Show-level money (guarantee, bonus tracking in pacing) — uses Shows granular permission. */
  const hideFinancialsShows = isFinancialHidden('shows')
  /** Intelligence dashboard Money card — uses Overview granular permission. */
  const hideFinancialsOverview = isFinancialHidden('overview')

  const [user] = useState<Record<string, unknown> | null>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? (JSON.parse(raw) as Record<string, unknown>) : null
    } catch {
      return null
    }
  })
  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  const isTeamMember = Boolean(user?.professionRole && typeof user.professionRole === 'string' && ['manager', 'support', 'promoter'].includes(user.professionRole))

  const shouldWaitForManagedArtists = Boolean(user?.email && token && isTeamMember)

  const [managedArtists, setManagedArtists] = useState<ManagedArtistRow[]>([])
  const [managedArtistsHydrated, setManagedArtistsHydrated] = useState(() => !shouldWaitForManagedArtists)

  useEffect(() => {
    if (!user?.email || !token) {
      setManagedArtistsHydrated(true)
      return
    }
    if (!isTeamMember) {
      setManagedArtistsHydrated(true)
      return
    }
    let cancelled = false
    postDataAPI('team/managed-artists', { teamMemberEmail: user.email }, token)
      .then((res) => {
        if (cancelled) return
        const raw = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(raw) ? raw : []
        setManagedArtists(list)
        setArtistCache(list)
      })
      .catch(() => {
        if (!cancelled) setManagedArtists([])
      })
      .finally(() => {
        if (!cancelled) setManagedArtistsHydrated(true)
      })
    return () => {
      cancelled = true
    }
  }, [user?.email, token, isTeamMember])
  /** Same list as Team → managed-artists (backend). Shown when you can act for more than one account. */
  const showArtistFilter = isTeamMember && managedArtists.length > 1

  const [intelScope, setIntelScope] = useState<'all' | string>(() => {
    try {
      const s = sessionStorage.getItem(INTEL_SCOPE_STORAGE_KEY)
      if (s === ALL_ARTISTS_VALUE) return 'all'
      if (s && s.length >= 8) return s
    } catch {
      /* ignore */
    }
    return ''
  })

  const resolvedDashboardArtistId =
    userId && managedArtists.some((a) => a._id === userId) ? userId : managedArtists[0]?._id ?? ''

  /** Cache + fetch key: combined view vs single-artist (primary or chosen id). */
  const scopeCacheKey = useMemo(() => {
    if (intelScope === 'all') {
      if (managedArtists.length === 0) return 'all:pending'
      return `all:${managedArtists.map((a) => a._id).sort().join(',')}`
    }
    if (intelScope && intelScope !== 'all') return intelScope
    return userId ?? ''
  }, [intelScope, managedArtists, userId])

  const handleDashboardArtistChange = (artistId: string) => {
    try {
      sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, artistId)
    } catch {
      /* ignore */
    }
    const current = getActiveArtistIds()
    const others = current.filter((id) => id !== artistId)
    setActiveArtistIds([artistId, ...others])
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        (key.startsWith('showsPageCache') ||
          key.startsWith('musicPageCatalogCache') ||
          key.startsWith('showsTicketTrackingOverrides') ||
          key.startsWith('pendingBandsintownShows'))
      ) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k))
    clearSessionStorageExceptIntelScope()
    window.dispatchEvent(new CustomEvent('dizee:activeArtistsChanged'))
  }

  const handleIntelArtistFilterChange = (value: string) => {
    if (value === ALL_ARTISTS_VALUE) {
      try {
        sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, ALL_ARTISTS_VALUE)
      } catch {
        /* ignore */
      }
      setIntelScope('all')
      clearSessionStorageExceptIntelScope()
      return
    }
    setIntelScope(value)
    handleDashboardArtistChange(value)
  }

  const artistSelectValue =
    intelScope === 'all'
      ? ALL_ARTISTS_VALUE
      : intelScope || resolvedDashboardArtistId || managedArtists[0]?._id || ''

  const cachedSignals = readSessionCache<IntelligenceSignal[]>(makeCacheKey([scopeCacheKey, 'intelligence', 'signals']))
  const cachedAt = readSessionCache<number>(makeCacheKey([scopeCacheKey, 'intelligence', 'cachedAt']))
  const hasFreshCache = cachedSignals && cachedAt && Date.now() - cachedAt < 5 * 60 * 1000

  const [signals, setSignals] = useState<IntelligenceSignal[]>(cachedSignals || [])
  const [isLoading, setIsLoading] = useState(!hasFreshCache)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(cachedAt ? new Date(cachedAt) : null)
  const [finances, setFinances] = useState<FinanceData[]>([])
  const [dashboardTasks, setDashboardTasks] = useState<DashboardTask[]>([])
  const [memberMap, setMemberMap] = useState<Record<string, string>>({})
  const [tmFlights, setTmFlights] = useState<unknown[]>([])
  const [tmHotels, setTmHotels] = useState<unknown[]>([])
  const [tmGroundTransport, setTmGroundTransport] = useState<unknown[]>([])

  const [heroRaw, setHeroRaw] = useState<HeroRawBundle | null>(() => {
    try {
      const shows = readSessionCache(makeCacheKey([userId, 'api', 'user/getBookings']))
      const songs = readSessionCache(makeCacheKey([userId, 'api', 'user/getCatalog']))
      const tasks = readSessionCache(makeCacheKey([userId, 'api', 'user/tasks']))
      const calendar =
        readSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=250'])) ??
        readSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']))
      if (shows || songs || tasks || calendar) {
        return {
          shows: shows ?? [],
          songs: songs ?? [],
          tasks: tasks ?? [],
          calendar: calendar ?? { data: { data: { events: [] } } },
        }
      }
    } catch {
      /* ignore */
    }
    return null
  })

  const applyData = (
    showsData: unknown,
    songsData: unknown,
    financialsData: unknown,
    tasksData: unknown,
    dealsData: unknown,
    cacheKey: string,
  ) => {
    const shows = normalizeBookings(showsData)
    const songs = normalizeCatalog(songsData)
    const finBase = normalizeFinancials(financialsData)
    const finDerived = [
      ...deriveShowFinanceEntries(showsData),
      ...deriveDealFinanceEntries(dealsData),
    ]
    const fin = mergeUniqueFinances(finBase, finDerived)
    const tasks = normalizeList<TaskData>(extractArray(tasksData) as TaskData[])

    setFinances(fin)
    setDashboardTasks(normalizeDashboardTasksFromPayload(tasksData))

    const computed = computeIntelligence({
      shows,
      songs,
      finances: fin,
      tasks,
    })
    setSignals(computed)
    setLastUpdated(new Date())
    writeSessionCache(makeCacheKey([cacheKey, 'intelligence', 'signals']), computed)
    writeSessionCache(makeCacheKey([cacheKey, 'intelligence', 'cachedAt']), Date.now())
  }

  /** Team: keep loading state true until managed-artists resolves (avoids empty dashboard flash when cache was keyed wrong). */
  useEffect(() => {
    if (isTeamMember && !managedArtistsHydrated) {
      setIsLoading(true)
    }
  }, [isTeamMember, managedArtistsHydrated])

  /** When scope key updates after managed-artists loads, re-hydrate signals from session cache (initial state used `all:pending`). */
  useEffect(() => {
    if (isTeamMember && !managedArtistsHydrated) return
    const cached = readSessionCache<IntelligenceSignal[]>(makeCacheKey([scopeCacheKey, 'intelligence', 'signals']))
    const at = readSessionCache<number>(makeCacheKey([scopeCacheKey, 'intelligence', 'cachedAt']))
    if (cached && at && Date.now() - at < 5 * 60 * 1000) {
      setSignals(cached)
      setLastUpdated(new Date(at))
      setIsLoading(false)
    }
  }, [scopeCacheKey, managedArtistsHydrated, isTeamMember])

  useEffect(() => {
    /** Team accounts: wait for managed-artists before fetching. Otherwise we hit the single-artist path with
     *  empty `managedArtists`, then re-fetch when the list loads — two skeleton / loading phases. */
    if (isTeamMember && !managedArtistsHydrated) {
      return
    }

    const fetchData = async () => {
      const effectiveUserId = getEffectiveUserId()
      try {
        if (intelScope === 'all' && managedArtists.length > 0) {
          const artistIds = managedArtists.map((a) => a._id)
          const bundles = await Promise.all(
            artistIds.map(async (aid) => {
              const params = { params: { userId: aid } }
              const [shows, songs, financials, tasks, dealsData, cal250, cal50] = await Promise.all([
                getDataAPI('user/getBookings', undefined, params).catch(() => []),
                getDataAPI('user/getCatalog', undefined, params).catch(() => []),
                getDataAPI('financials', undefined, params).catch(() => []),
                getDataAPI('user/tasks?scope=all', undefined, params).catch(() =>
                  getDataAPI('user/tasks', undefined, params).catch(() => []),
                ),
                (async () => {
                  try {
                    return await getDataAPI('deals/revenue-entries', undefined, params)
                  } catch {
                    try {
                      return await getDataAPI('deals', undefined, params)
                    } catch {
                      return { data: { deals: [] } }
                    }
                  }
                })(),
                getDataAPI('user/connectors/googleCalendar/events?maxResults=250', undefined, params).catch(() => null),
                getDataAPI('user/connectors/googleCalendar/events?maxResults=50', undefined, params).catch(() => null),
              ])
              return { shows, songs, financials, tasks, dealsData, cal250, cal50 }
            }),
          )

          const shows = mergeRawPayloadArrays(bundles.map((b) => b.shows))
          const songs = mergeRawPayloadArrays(bundles.map((b) => b.songs))
          const financials = mergeRawPayloadArrays(bundles.map((b) => b.financials))
          const tasks = mergeRawPayloadArrays(bundles.map((b) => b.tasks))
          const dealsData = mergeDealsPayloads(bundles.map((b) => b.dealsData))
          const calendarData = mergeCalendarPayloads(bundles.map((b) => b.cal250 ?? b.cal50))

          setHeroRaw({ shows, songs, tasks, calendar: calendarData })
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getBookings']), shows)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getCatalog']), songs)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'financials']), financials)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/tasks']), tasks)
          writeSessionCache(
            makeCacheKey([scopeCacheKey, 'api', 'user/connectors/googleCalendar/events?maxResults=250']),
            calendarData,
          )
          applyData(shows, songs, financials, tasks, dealsData, scopeCacheKey)

          const map: Record<string, string> = {}
          for (const aid of artistIds) {
            try {
              const response = await postDataAPI('team/members', { userId: aid, userRole: 'artist' })
              const data = response?.data?.data ?? response?.data ?? response
              const arr = Array.isArray(data) ? data : []
              for (const member of arr) {
                if (member.status !== 'active') continue
                const id = String(member._id ?? member.id)
                map[id] = member.name ?? member.username ?? member.email ?? id
              }
            } catch {
              /* ignore */
            }
          }
          try {
            const raw = localStorage.getItem('dizeeUser')
            const u = raw ? JSON.parse(raw) : null
            if (u?._id) map[String(u._id)] = u.name ?? u.username ?? map[String(u._id)]
          } catch {
            /* ignore */
          }
          setMemberMap(map)
          try {
            const [tf, th, tg] = await Promise.all([
              getDataAPI('user/tm/flights').catch(() => []),
              getDataAPI('user/tm/hotels').catch(() => []),
              getDataAPI('user/tm/ground-transport').catch(() => []),
            ])
            setTmFlights(extractArray(tf))
            setTmHotels(extractArray(th))
            setTmGroundTransport(extractArray(tg))
          } catch {
            setTmFlights([])
            setTmHotels([])
            setTmGroundTransport([])
          }
          return
        }

        const singleParams =
          intelScope && intelScope !== 'all' ? { params: { userId: intelScope } } : undefined

        const [shows, songs, financials, tasks, dealsData, cal250, cal50] = await Promise.all([
          getDataAPI('user/getBookings', undefined, singleParams).catch(() => []),
          getDataAPI('user/getCatalog', undefined, singleParams).catch(() => []),
          getDataAPI('financials', undefined, singleParams).catch(() => []),
          getDataAPI('user/tasks?scope=all', undefined, singleParams).catch(() =>
            getDataAPI('user/tasks', undefined, singleParams).catch(() => []),
          ),
          (async () => {
            try {
              return await getDataAPI('deals/revenue-entries', undefined, singleParams)
            } catch {
              try {
                return await getDataAPI('deals', undefined, singleParams)
              } catch {
                return { data: { deals: [] } }
              }
            }
          })(),
          getDataAPI('user/connectors/googleCalendar/events?maxResults=250', undefined, singleParams).catch(() => null),
          getDataAPI('user/connectors/googleCalendar/events?maxResults=50', undefined, singleParams).catch(() => null),
        ])
        const calendarData = cal250 ?? cal50 ?? { data: { data: { events: [] } } }
        setHeroRaw({ shows, songs, tasks, calendar: calendarData })
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getBookings']), shows)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getCatalog']), songs)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'financials']), financials)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/tasks']), tasks)
        const calKey =
          cal250 != null
            ? 'user/connectors/googleCalendar/events?maxResults=250'
            : 'user/connectors/googleCalendar/events?maxResults=50'
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', calKey]), calendarData)
        applyData(shows, songs, financials, tasks, dealsData, scopeCacheKey)

        try {
          const [tf, th, tg] = await Promise.all([
            getDataAPI('user/tm/flights', undefined, singleParams).catch(() => []),
            getDataAPI('user/tm/hotels', undefined, singleParams).catch(() => []),
            getDataAPI('user/tm/ground-transport', undefined, singleParams).catch(() => []),
          ])
          setTmFlights(extractArray(tf))
          setTmHotels(extractArray(th))
          setTmGroundTransport(extractArray(tg))
        } catch {
          setTmFlights([])
          setTmHotels([])
          setTmGroundTransport([])
        }

        const memberUserId = intelScope && intelScope !== 'all' ? intelScope : effectiveUserId
        if (memberUserId) {
          try {
            const response = await postDataAPI('team/members', { userId: memberUserId, userRole: 'artist' })
            const data = response?.data?.data ?? response?.data ?? response
            const arr = Array.isArray(data) ? data : []
            const map: Record<string, string> = {}
            for (const member of arr) {
              if (member.status !== 'active') continue
              const id = String(member._id ?? member.id)
              map[id] = member.name ?? member.username ?? member.email ?? id
            }
            try {
              const raw = localStorage.getItem('dizeeUser')
              const u = raw ? JSON.parse(raw) : null
              if (u?._id) map[String(u._id)] = u.name ?? u.username ?? map[String(u._id)]
            } catch {
              /* ignore */
            }
            setMemberMap(map)
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err)
        setHeroRaw({
          shows: [],
          songs: [],
          tasks: [],
          calendar: { data: { data: { events: [] } } },
        })
        setTmFlights([])
        setTmHotels([])
        setTmGroundTransport([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
    const interval = setInterval(fetchData, 60_000)
    return () => clearInterval(interval)
  }, [userId, artistScopeTick, intelScope, managedArtists, scopeCacheKey, isTeamMember, managedArtistsHydrated])

  const byType = useMemo(() => {
    const m: Record<string, IntelligenceSignal[]> = {}
    for (const s of signals) {
      const key = s.type
      if (!m[key]) m[key] = []
      m[key].push(s)
    }
    return m
  }, [signals])

  const riskSignals = byType['at-risk'] || []
  const pacingSignals = useMemo(() => {
    const list = [...(byType['pacing'] || [])]
    list.sort((a, b) => (a.meta?.daysUntil ?? 999) - (b.meta?.daysUntil ?? 999))
    return list
  }, [byType])
  const streamingSignals = byType['streaming'] || []

  const heroModel = useMemo(() => {
    if (!heroRaw) return null
    return computeDashboardHeroCardModel(heroRaw.shows, heroRaw.songs, heroRaw.tasks)
  }, [heroRaw])

  const showsById = useMemo(() => {
    if (!heroRaw) return new Map<string, ShowData>()
    const shows = normalizeBookings(heroRaw.shows)
    return new Map(shows.map((s) => [String(s.id), s]))
  }, [heroRaw])

  const catalogSongs = useMemo(() => {
    if (!heroRaw) return []
    return normalizeCatalog(heroRaw.songs)
  }, [heroRaw])

  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [assetsModalOpen, setAssetsModalOpen] = useState(false)

  const [moduleLayout, setModuleLayout] = useState<DashboardModuleLayout>(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_MODULE_LAYOUT_KEY)
      if (raw === 'stacked' || raw === 'sideBySide') return raw
    } catch {
      /* ignore */
    }
    return 'sideBySide'
  })

  const [moduleOrder, setModuleOrder] = useState<DashboardModuleId[]>(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_MODULE_ORDER_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as unknown
        return normalizeDashboardModuleOrder(parsed)
      }
    } catch {
      /* ignore */
    }
    return [...ALL_MODULE_IDS]
  })

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_MODULE_LAYOUT_KEY, moduleLayout)
    } catch {
      /* ignore */
    }
  }, [moduleLayout])

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_MODULE_ORDER_KEY, JSON.stringify(moduleOrder))
    } catch {
      /* ignore */
    }
  }, [moduleOrder])

  const isDesktopModules = useDashboardModuleDesktop()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleModuleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setModuleOrder((items) => {
        const oldIndex = items.indexOf(active.id as DashboardModuleId)
        const newIndex = items.indexOf(over.id as DashboardModuleId)
        if (oldIndex === -1 || newIndex === -1) return items
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const moduleGridClass =
    moduleLayout === 'stacked'
      ? 'grid grid-cols-1 gap-4 min-w-0'
      : 'grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0'

  const moduleSortStrategy =
    moduleLayout === 'stacked' ? verticalListSortingStrategy : rectSortingStrategy

  const skeletonModuleGridClass = isDesktopModules
    ? moduleGridClass
    : 'grid grid-cols-1 gap-4 min-w-0'

  const renderModuleContent = (id: DashboardModuleId) => {
    switch (id) {
      case 'shows':
        return (
          <DashboardShowsCombined
            pacingSignals={pacingSignals}
            riskSignals={riskSignals}
            showsById={showsById}
            hideFinancials={hideFinancialsShows}
            tmFlights={tmFlights}
            tmHotels={tmHotels}
            tmGroundTransport={tmGroundTransport}
          />
        )
      case 'music':
        return <DashboardMusicCombined catalogSongs={catalogSongs} streamingSignals={streamingSignals} />
      case 'tasks':
        return <DashboardTasksWeek tasks={dashboardTasks} memberMap={memberMap} />
      case 'money':
        return (
          <DashboardMoneySection finances={hideFinancialsOverview ? [] : finances} hideFinancials={hideFinancialsOverview} />
        )
      default:
        return null
    }
  }

  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto overflow-x-hidden">
      <UploadDataModal isOpen={uploadModalOpen} onClose={() => setUploadModalOpen(false)} />
      <DashboardAssetsModal isOpen={assetsModalOpen} onClose={() => setAssetsModalOpen(false)} userId={userId} />

      <div className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 pt-16 md:pt-0 pb-4 bg-[rgb(var(--bg-rgb))]">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · refreshes every 60s`
                : 'Loading…'}
            </p>
          </div>
          <div className="flex flex-col w-full md:w-auto md:flex-row md:flex-wrap items-stretch md:items-center gap-2 shrink-0">
            {isTeamMember && !managedArtistsHydrated && (
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto" aria-busy aria-label="Loading artist list">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Artist</span>
                <div className="h-10 min-w-[10rem] max-w-[min(100%,16rem)] rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
              </div>
            )}
            {showArtistFilter && managedArtistsHydrated ? (
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
                <label htmlFor="intel-dashboard-artist" className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  Artist
                </label>
                <select
                  id="intel-dashboard-artist"
                  value={artistSelectValue}
                  onChange={(e) => handleIntelArtistFilterChange(e.target.value)}
                  className="ds-select text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 min-w-[10rem] max-w-[min(100%,16rem)] focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400"
                >
                  <option value={ALL_ARTISTS_VALUE}>All artists</option>
                  {managedArtists.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate('/digest')}
              onMouseEnter={prefetchDigestPage}
              onFocus={prefetchDigestPage}
              className="w-full md:w-auto justify-center"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                See Digest
              </span>
            </Button>
            <Button variant="secondary" onClick={() => setAssetsModalOpen(true)} className="w-full md:w-auto justify-center">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                Assets
              </span>
            </Button>
            <Button variant="secondary" onClick={() => setUploadModalOpen(true)} className="w-full md:w-auto justify-center">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Data
              </span>
            </Button>
          </div>
        </div>
      </div>

      {((isLoading && signals.length === 0) || (isTeamMember && !managedArtistsHydrated)) && (
        <>
          <div className="mb-8 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-3/4 max-w-md" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700">
              {[0, 1, 2].map((i) => (
                <div key={i} className="p-4 sm:p-6 space-y-2">
                  <Skeleton className="h-2.5 w-20" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              ))}
            </div>
          </div>
          <div className={skeletonModuleGridClass}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 min-h-40 flex flex-col gap-3"
              >
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[85%]" />
                <Skeleton className="h-20 w-full rounded-lg mt-auto" />
              </div>
            ))}
          </div>
        </>
      )}

      {(signals.length > 0 || !isLoading) && managedArtistsHydrated && (
        <>
          {heroModel && (
            <DashboardHeroCard
              greeting={heroModel.greeting}
              todayTasks={heroModel.todayTasks}
              tasksHref={heroModel.tasksHref}
              nextShow={heroModel.nextShow}
              nextRelease={heroModel.nextRelease}
              sectionClassName="mb-8"
            />
          )}

          {isDesktopModules ? (
            <>
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setModuleLayout((m) => (m === 'stacked' ? 'sideBySide' : 'stacked'))}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:border-white/[0.1] dark:bg-[#13120A] dark:text-zinc-300 dark:hover:bg-white/[0.06] dark:focus-visible:ring-gray-400"
                  title={moduleLayout === 'stacked' ? 'Switch to side-by-side columns' : 'Switch to stacked layout'}
                  aria-label={
                    moduleLayout === 'stacked' ? 'Switch to side-by-side columns' : 'Switch to stacked layout'
                  }
                >
                  {moduleLayout === 'stacked' ? (
                    <Columns2 className="h-4 w-4" aria-hidden />
                  ) : (
                    <LayoutList className="h-4 w-4" aria-hidden />
                  )}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-500 sm:text-right">
                  Hover the top-right of a card to drag and rearrange.
                </p>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
                <SortableContext items={moduleOrder} strategy={moduleSortStrategy}>
                  <div className={moduleGridClass}>
                    {moduleOrder.map((id) => (
                      <SortableModuleSection key={id} id={id} title={MODULE_TITLES[id]}>
                        {renderModuleContent(id)}
                      </SortableModuleSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4 min-w-0">
              {moduleOrder.map((id) => (
                <SectionCard key={id} title={MODULE_TITLES[id]}>
                  {renderModuleContent(id)}
                </SectionCard>
              ))}
            </div>
          )}

          <div className="mt-6 mb-4 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Dashboard signals are factual observations from your data — not recommendations.
            </p>
          </div>

          <style>{`
            .intel-scroll {
              scrollbar-gutter: stable;
              scrollbar-width: thin;
              scrollbar-color: rgba(156,163,175,0.55) transparent;
            }
            .intel-scroll::-webkit-scrollbar {
              width: 6px;
            }
            .intel-scroll::-webkit-scrollbar-track {
              background: transparent;
            }
            .intel-scroll::-webkit-scrollbar-thumb {
              background: rgba(156,163,175,0.45);
              border-radius: 4px;
            }
            .intel-scroll::-webkit-scrollbar-thumb:hover {
              background: rgba(156,163,175,0.65);
            }
            .dark .intel-scroll {
              scrollbar-color: rgba(107,114,128,0.6) transparent;
            }
            .dark .intel-scroll::-webkit-scrollbar-thumb {
              background: rgba(107,114,128,0.45);
            }
            .dark .intel-scroll::-webkit-scrollbar-thumb:hover {
              background: rgba(107,114,128,0.65);
            }
          `}</style>
        </>
      )}
    </div>
  )
}
